import {useEffect, useState, type ReactNode} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Globe,
  Plus,
  ExternalLink,
  Loader2,
  ArrowRight,
  Search,
  X,
  Rocket,
  FilePen,
  RefreshCw,
  Building2,
  ChevronLeft,
  ChevronRight,
  MapPin,
  MoreHorizontal,
  SquarePen,
  History,
  Check,
  Trash2,
} from 'lucide-react';
import {trpc, RouterOutput, createMutationErrorHandler} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {CODE_PERMISSIONS} from '@common/permissions';
import {DetailHeader} from '@internal/components/DetailHeader';
import {EnrollmentSitesTabs} from '@internal/pages/enrollment/EnrollmentSitesTabs';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {HoverCard, HoverCardTrigger, HoverCardContent} from '@common-web/components/ui/hover-card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@common-web/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@common-web/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';

type EmployersOutput = RouterOutput['internal']['enrollmentSite']['employers'];
type EmployerRow = EmployersOutput['rows'][number];
type EmployerStatus = EmployerRow['status'];

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];

const STATUS_LABEL: Record<EmployerStatus, string> = {
  NOT_STARTED: 'Not started',
  DRAFT: 'Draft',
  LIVE: 'Live',
  LIVE_EDITS: 'Live · edits',
};

// Semantic tints (flattened by the internal portal): grey / blue / green / amber.
const STATUS_VARIANT: Record<EmployerStatus, 'outline' | 'info' | 'success' | 'warning'> = {
  NOT_STARTED: 'outline',
  DRAFT: 'info',
  LIVE: 'success',
  LIVE_EDITS: 'warning',
};

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// The row's primary label. Count-led rollups (large, no parseable name) show
// their location count instead of a misleading representative name.
function primaryLabel(row: EmployerRow): string {
  return row.nameKind === 'count' ? `${row.locationCount} locations` : row.name;
}

export function EnrollmentSitesList() {
  const navigate = useNavigate();
  const canManage = useHasPermission(CODE_PERMISSIONS.enrollment.manage.key);
  const utils = trpc.useUtils();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [newOpen, setNewOpen] = useState(false);
  const [deleting, setDeleting] = useState<EmployerRow | null>(null);

  const deleteSite = trpc.internal.enrollmentSite.delete.useMutation({
    onSuccess: () => {
      utils.internal.enrollmentSite.employers.invalidate();
      toast({title: 'Draft deleted'});
      setDeleting(null);
    },
    onError: createMutationErrorHandler('Could not delete the draft'),
  });

  // Debounce the search box; reset to page 1 whenever the term changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const {data, isLoading, isFetching} = trpc.internal.enrollmentSite.employers.useQuery({
    search,
    page,
    pageSize,
  });

  const stats = data?.stats;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Build drops into the builder unsaved, seeded from the employer (name/slug)
  // and stamped with its rollup key so the saved draft links back to this row.
  function startBuild(e: Pick<EmployerRow, 'key' | 'name' | 'nameKind'>) {
    const name = e.nameKind === 'count' ? '' : e.name;
    const params = new URLSearchParams({name, slug: slugify(e.name), employer: e.key});
    navigate(`/enrollment-sites/new?${params.toString()}`);
  }

  function openRow(row: EmployerRow) {
    if (row.groupSlug) navigate(`/enrollment-sites/${row.groupSlug}`);
    else if (canManage) startBuild(row);
  }

  return (
    <div className='space-y-4 px-6 py-6'>
      <DetailHeader
        icon={<Globe className='h-5 w-5' />}
        title='Enrollment Sites'
        subtitle='Build, publish, and track every employer’s enrollment site.'
        actions={
          canManage ? (
            <Button onClick={() => setNewOpen(true)}>
              <Plus className='mr-1.5 h-4 w-4' />
              New site
            </Button>
          ) : undefined
        }
      />

      <EnrollmentSitesTabs />

      {/* Stat widgets */}
      <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
        <StatCard icon={<Rocket className='h-4 w-4' />} label='Live' value={stats?.live} tone='emerald' />
        <StatCard icon={<FilePen className='h-4 w-4' />} label='In progress' value={stats?.draft} tone='amber' />
        <StatCard
          icon={<RefreshCw className='h-4 w-4' />}
          label='Has pending edits'
          value={stats?.liveEdits}
          tone='sky'
        />
        <StatCard
          icon={<Building2 className='h-4 w-4' />}
          label='Total employers'
          value={stats?.total}
          tone='slate'
        />
      </div>

      {/* Search */}
      <div className='flex items-center justify-between gap-3'>
        <div className='relative w-full max-w-sm'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder='Search employers or locations...'
            className='pl-9 pr-9'
          />
          {searchInput && (
            <button
              type='button'
              onClick={() => setSearchInput('')}
              aria-label='Clear search'
              className='absolute right-2.5 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground'
            >
              <X className='h-4 w-4' />
            </button>
          )}
        </div>
        <span className='shrink-0 text-sm text-muted-foreground'>
          {total.toLocaleString()} employer{total === 1 ? '' : 's'}
        </span>
      </div>

      <p className='-mt-2 text-xs text-muted-foreground'>
        Multi-location employers show as one row; hover the location count to see them all.
      </p>

      <div className='overflow-hidden rounded-lg border bg-white'>
        <div className='overflow-x-auto'>
        {/* min-w keeps the flexible Employer column ~220px before the table scrolls. */}
        <table className='w-full min-w-[660px] table-fixed text-sm'>
          <thead className='border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground'>
            <tr>
              {/* Employer takes the remaining width and truncates; the rest are fixed. */}
              <th className='min-w-[220px] px-6 py-3 font-semibold'>Employer</th>
              <th className='w-[160px] px-6 py-3 text-center font-semibold'>Status</th>
              <th className='w-[96px] px-6 py-3 font-semibold'>Locations</th>
              <th className='w-[80px] px-6 py-3 font-semibold'>Version</th>
              <th className='w-[96px] px-6 py-3' />
            </tr>
          </thead>
          <tbody className={isFetching ? 'opacity-60 transition-opacity' : undefined}>
            {isLoading ? (
              <tr>
                <td colSpan={5} className='py-16 text-center'>
                  <Loader2 className='mx-auto h-6 w-6 animate-spin text-muted-foreground' />
                </td>
              </tr>
            ) : !data?.rows.length ? (
              <tr>
                <td colSpan={5} className='py-16 text-center text-sm text-muted-foreground'>
                  {search ? `No employers match “${search}”.` : 'No employers yet.'}
                </td>
              </tr>
            ) : (
              data.rows.map((row) => (
                <tr
                  key={row.key}
                  onClick={() => openRow(row)}
                  className='cursor-pointer border-b last:border-0 hover:bg-muted/30'
                >
                  <td className='px-6 py-3.5'>
                    <div className='flex items-center gap-3'>
                      <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-100 text-xs font-semibold text-slate-600'>
                        {initials(primaryLabel(row))}
                      </span>
                      <div className='min-w-0'>
                        <div className='flex items-center gap-2'>
                          <span
                            className={
                              'truncate font-medium ' +
                              (row.nameKind === 'representative' || row.nameKind === 'count'
                                ? 'italic text-foreground/90'
                                : '')
                            }
                          >
                            {primaryLabel(row)}
                          </span>
                          {row.isMultiLocation && (
                            <LocationsHover row={row}>
                              <span onClick={(e) => e.stopPropagation()} className='shrink-0 cursor-default'>
                                <Badge variant='secondary' className='shrink-0 gap-1 text-[10px] font-normal'>
                                  <MapPin className='h-3 w-3' />
                                  Multi-location
                                </Badge>
                              </span>
                            </LocationsHover>
                          )}
                        </div>
                        <RowSubtext row={row} />
                      </div>
                    </div>
                  </td>
                  <td className='px-6 py-3.5'>
                    <div className='flex flex-col items-center gap-1'>
                      <Badge variant={STATUS_VARIANT[row.status]} className='whitespace-nowrap'>
                        {STATUS_LABEL[row.status]}
                      </Badge>
                      {row.status === 'DRAFT' && row.lastActivity && (
                        <span className='text-xs text-muted-foreground'>
                          Updated{' '}
                          {new Date(row.lastActivity).toLocaleDateString(undefined, {
                            month: 'numeric',
                            day: 'numeric',
                            year: '2-digit',
                          })}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className='px-6 py-3.5 text-muted-foreground'>
                    {row.isMultiLocation ? (
                      <LocationsHover row={row}>
                        <span
                          onClick={(e) => e.stopPropagation()}
                          className='inline-flex cursor-default items-center gap-1 underline decoration-dotted underline-offset-2'
                        >
                          <MapPin className='h-3.5 w-3.5' />
                          {row.locationCount}
                        </span>
                      </LocationsHover>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className='px-6 py-3.5 text-muted-foreground'>
                    {row.version ? `v${row.version}` : '—'}
                  </td>
                  <td className='whitespace-nowrap px-6 py-3.5 text-right'>
                    {row.groupSlug ? (
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-8 w-8 p-0'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className='h-4 w-4' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align='end'
                          className='w-48'
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenuItem
                            className='cursor-pointer gap-2'
                            onClick={() => navigate(`/enrollment-sites/${row.groupSlug}`)}
                          >
                            <SquarePen className='h-4 w-4 text-muted-foreground' />
                            Open builder
                          </DropdownMenuItem>
                          {row.requiresAccessCode && (
                            <DropdownMenuItem
                              className='cursor-pointer gap-2'
                              onClick={() => navigate(`/enrollment-sites/${row.groupSlug}/locations?from=sites`)}
                            >
                              <MapPin className='h-4 w-4 text-muted-foreground' />
                              Manage access codes
                            </DropdownMenuItem>
                          )}
                          {row.version ? (
                            <DropdownMenuItem
                              className='cursor-pointer gap-2'
                              onClick={() => navigate(`/enrollment-sites/${row.groupSlug}?history=1`)}
                            >
                              <History className='h-4 w-4 text-muted-foreground' />
                              Version history
                            </DropdownMenuItem>
                          ) : null}
                          {row.liveUrl && (
                            <DropdownMenuItem asChild className='cursor-pointer gap-2'>
                              <a href={row.liveUrl} target='_blank' rel='noreferrer'>
                                <ExternalLink className='h-4 w-4 text-muted-foreground' />
                                Visit live site
                              </a>
                            </DropdownMenuItem>
                          )}
                          {row.status === 'DRAFT' && (
                            <DropdownMenuItem
                              className='cursor-pointer gap-2 text-destructive focus:text-destructive'
                              onClick={() => setDeleting(row)}
                            >
                              <Trash2 className='h-4 w-4' />
                              Delete draft
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : canManage ? (
                      <button
                        type='button'
                        onClick={(e) => {
                          e.stopPropagation();
                          openRow(row);
                        }}
                        className='inline-flex items-center gap-1 text-xs font-medium text-primary'
                      >
                        Build <ArrowRight className='h-3.5 w-3.5' />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        {/* Pagination — inside the card so it shares the table's surface. */}
        <div className='flex items-center justify-between border-t px-6 py-3 text-sm text-muted-foreground'>
          <span>
            {total === 0
              ? 'No results'
              : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString()}`}
          </span>
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-2'>
            <span>Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className='h-8 w-[70px]'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span>
            Page {page} of {totalPages}
          </span>
          <Button variant='outline' size='sm' disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            size='sm'
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      </div>
      </div>

      {newOpen && (
        <NewSiteDialog
          onClose={() => setNewOpen(false)}
          onSelect={(employer) => {
            setNewOpen(false);
            startBuild(employer);
          }}
        />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleting?.name}” and any access codes set up for it will be permanently removed. This
              can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSite.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
              disabled={deleteSite.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleting?.groupSlug) deleteSite.mutate({groupSlug: deleting.groupSlug});
              }}
            >
              {deleteSite.isPending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
              Delete draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Under the employer name: the site slug once started, else a hint of what the
// rollup contains (sample locations for unnamed rollups, a count for named ones).
function RowSubtext({row}: {row: EmployerRow}) {
  if (row.groupSlug) {
    return <div className='truncate text-xs text-muted-foreground'>/{row.groupSlug}</div>;
  }
  if (row.nameKind === 'count' || row.nameKind === 'representative') {
    return (
      <div className='truncate text-xs text-muted-foreground'>{row.sampleLocations.join(' · ')}</div>
    );
  }
  if (row.isMultiLocation) {
    return <div className='text-xs text-muted-foreground'>{row.locationCount} locations</div>;
  }
  return null;
}

const TONE: Record<string, string> = {
  emerald: 'text-emerald-600',
  amber: 'text-amber-600',
  sky: 'text-sky-600',
  slate: 'text-slate-500',
};

function StatCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | undefined;
  tone: keyof typeof TONE | string;
}) {
  return (
    <div className='rounded-lg border bg-white p-4'>
      <div className={'flex items-center gap-1.5 text-xs font-medium ' + (TONE[tone] ?? '')}>
        {icon}
        {label}
      </div>
      <div className='mt-1 text-2xl font-semibold'>
        {value === undefined ? '—' : value.toLocaleString()}
      </div>
    </div>
  );
}

// Wraps a trigger so hovering it reveals the employer's full location list,
// lazy-fetched so big rollups don't bloat the list response. Used by both the
// Multi-location badge and the location count.
function LocationsHover({row, children}: {row: EmployerRow; children: ReactNode}) {
  const [open, setOpen] = useState(false);
  const {data, isLoading} = trpc.internal.enrollmentSite.employerLocations.useQuery(
    {key: row.key},
    {enabled: open},
  );

  return (
    <HoverCard openDelay={150} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent align='start' className='max-h-72 w-72 overflow-y-auto'>
        <div className='mb-1 text-xs font-semibold text-muted-foreground'>
          {row.locationCount} locations
        </div>
        {isLoading || !data ? (
          <div className='py-3 text-center'>
            <Loader2 className='mx-auto h-4 w-4 animate-spin text-muted-foreground' />
          </div>
        ) : (
          <ul className='space-y-0.5 text-sm'>
            {data.map((loc, i) => (
              <li key={i} className='truncate'>
                {loc.name}
              </li>
            ))}
          </ul>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

function NewSiteDialog({
  onClose,
  onSelect,
}: {
  onClose: () => void;
  onSelect: (employer: EmployerRow) => void;
}) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reuse the employers query, scoped server-side to employers without a site.
  const {data, isFetching} = trpc.internal.enrollmentSite.employers.useQuery({
    search,
    notStarted: true,
    page: 1,
    pageSize: 25,
  });
  const rows = data?.rows ?? [];
  const selected = rows.find((r) => r.key === selectedKey);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-md' aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>New enrollment site</DialogTitle>
        </DialogHeader>

        <div className='relative'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            autoFocus
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder='Search employers...'
            className='pl-9'
          />
        </div>

        {/* Fixed height so the empty/loading states don't shift the dialog. */}
        <div className='h-72 overflow-y-auto rounded-md border'>
          {isFetching && !rows.length ? (
            <div className='flex h-full items-center justify-center'>
              <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
            </div>
          ) : rows.length === 0 ? (
            <div className='flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground'>
              {search ? 'No employers without a site match.' : 'Type to search employers.'}
            </div>
          ) : (
            <ul className='divide-y'>
              {rows.map((r) => {
                const isSelected = r.key === selectedKey;
                return (
                  <li key={r.key}>
                    <button
                      type='button'
                      onClick={() => setSelectedKey(r.key)}
                      className={
                        'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ' +
                        (isSelected ? 'bg-primary/10' : 'hover:bg-muted')
                      }
                    >
                      <span className='flex min-w-0 items-center gap-2'>
                        <Check
                          className={'h-4 w-4 shrink-0 text-primary ' + (isSelected ? '' : 'opacity-0')}
                        />
                        <span className='truncate'>{primaryLabel(r)}</span>
                      </span>
                      {r.isMultiLocation && (
                        <span className='shrink-0 text-xs text-muted-foreground'>
                          {r.locationCount} locations
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!selected} onClick={() => selected && onSelect(selected)}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
